import { type FC, type JSX, useState } from "react"
import styled from "styled-components"

import { type PurchaseableType } from "@forento/shared/models/product"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import Button from "~/themes/original/components/Button"
import Hero, { CallToActionButtons, CallToActionInfo } from "~/themes/original/components/Hero"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import routes from "~/utilities/routes"

type Product = {
	id: number
	name: string
	thumbnailUrl: string | null
	shortDescription: string | null
	longDescriptionHtml: string | null
	presentationHtml: string | null
	tags?: { id: number; label: string }[]
} & (
	| { status: "sign-in-required" | "membership-required" | "paused" | "unlocked" }
	| { status: "paywall"; price: { amount: number; currency: string } }
)

type Props = {
	type: PurchaseableType
	product: Product
	unlockedContent: JSX.Element
	reload(): Promise<unknown>
}
const ProductDetail: FC<Props> = ({ type, product, unlockedContent, reload }) => {
	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	return (
		<PurchaseSuccessModalContextProvider content={{ type, id: product.id }} paypalOrder={paypalOrder}>
			<Hero
				title={product.name}
				thumbnailUrl={product.thumbnailUrl}
				htmlDescription={product.longDescriptionHtml ?? null}
				tags={product.tags}
				callToAction={
					product.status === "paywall" ? (
						<PurchaseItem
							item={{
								type,
								id: product.id,
								name: product.name,
								shortDescription: product.shortDescription,
								price: product.price,
								accessTimeMonths: null,
							}}
							onPurchase={async ({ paypalOrder }) => {
								setPaypalOrder(paypalOrder)
								await reload()
							}}
						/>
					) : product.status === "sign-in-required" ? (
						<>
							<CallToActionInfo>This downloadable is only available to signed in users.</CallToActionInfo>
							<CallToActionButtons>
								<Button variant="primary" onClick={routes.user.signin()}>
									Sign in
								</Button>
								<Button variant="primary" onClick={routes.user.signup()}>
									Sign up
								</Button>
							</CallToActionButtons>
						</>
					) : product.status === "membership-required" ? (
						<>
							<CallToActionInfo>This downloadable is only available to members.</CallToActionInfo>
							<CallToActionButtons>
								<Button variant="primary" onClick={routes.membership.index()}>
									Memberships
								</Button>
							</CallToActionButtons>
						</>
					) : product.status === "paused" ? (
						<CallToActionInfo>Sales are paused.</CallToActionInfo>
					) : product.status === "unlocked" ? (
						unlockedContent
					) : (
						exhaustiveGuard(product.status)
					)
				}
			/>
			{product.presentationHtml && (
				<Presentation className="editorjs" dangerouslySetInnerHTML={{ __html: product.presentationHtml }} />
			)}
		</PurchaseSuccessModalContextProvider>
	)
}

const Presentation = styled.div`
	@media (max-width: 999px) {
		padding: 0 16px;
	}
`

export default ProductDetail
